import { useState, useEffect, useCallback } from 'react';
import {
  institutionRecycleBin,
  type DeletedDocument,
} from '@/services/institution';
import { Modal } from '@/components/common/Modal';
import { Button } from '@/components/common/Button';
import styles from './RecycleBinPage.module.css';

export function RecycleBinPage() {
  const [documents, setDocuments] = useState<DeletedDocument[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalCount, setTotalCount] = useState(0);

  // Selection state
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  // Modal state
  const [restoreDoc, setRestoreDoc] = useState<DeletedDocument | null>(null);
  const [deleteDoc, setDeleteDoc] = useState<DeletedDocument | null>(null);
  const [showEmptyConfirm, setShowEmptyConfirm] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const response = await institutionRecycleBin.list({ page, perPage: 20 });
      if (response.success && response.data) {
        setDocuments(response.data);
        if (response.meta) {
          setTotalPages(response.meta.totalPages);
          setTotalCount(response.meta.total);
        }
      }
    } catch (err) {
      console.error('Failed to fetch deleted documents:', err);
    } finally {
      setLoading(false);
    }
  }, [page]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  function toggleSelection(id: string) {
    const newSet = new Set(selectedIds);
    if (newSet.has(id)) {
      newSet.delete(id);
    } else {
      newSet.add(id);
    }
    setSelectedIds(newSet);
  }

  function toggleSelectAll() {
    if (selectedIds.size === documents.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(documents.map(d => d.id)));
    }
  }

  async function handleRestore(doc: DeletedDocument) {
    setProcessing(true);
    setError(null);
    try {
      const response = await institutionRecycleBin.restore(doc.id);
      if (response.success) {
        setRestoreDoc(null);
        fetchDocuments();
      } else {
        setError(response.error?.message || 'Failed to restore document');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setProcessing(false);
    }
  }

  async function handlePermanentDelete(doc: DeletedDocument) {
    setProcessing(true);
    setError(null);
    try {
      const response = await institutionRecycleBin.permanentDelete(doc.id);
      if (response.success) {
        setDeleteDoc(null);
        fetchDocuments();
      } else {
        setError(response.error?.message || 'Failed to delete document');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setProcessing(false);
    }
  }

  async function handleEmptyBin() {
    setProcessing(true);
    setError(null);
    try {
      const response = await institutionRecycleBin.emptyBin();
      if (response.success) {
        setShowEmptyConfirm(false);
        fetchDocuments();
      } else {
        setError(response.error?.message || 'Failed to empty recycle bin');
      }
    } catch (err) {
      setError('An error occurred');
    } finally {
      setProcessing(false);
    }
  }

  function formatDate(dateStr: string): string {
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  function getDaysUntilPermanentDelete(deletedAt: string): number {
    const deleteDate = new Date(deletedAt);
    const permanentDeleteDate = new Date(deleteDate);
    permanentDeleteDate.setDate(permanentDeleteDate.getDate() + 30);
    const now = new Date();
    const daysRemaining = Math.ceil((permanentDeleteDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return Math.max(0, daysRemaining);
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <div className={styles.headerContent}>
          <h1 className={styles.title}>Recycle Bin</h1>
          <p className={styles.subtitle}>
            {totalCount > 0
              ? `${totalCount} deleted item${totalCount !== 1 ? 's' : ''} - Items are permanently deleted after 30 days`
              : 'Deleted documents will appear here'}
          </p>
        </div>
        {documents.length > 0 && (
          <Button variant="danger" onClick={() => setShowEmptyConfirm(true)}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
            </svg>
            Empty Recycle Bin
          </Button>
        )}
      </div>

      <div className={styles.content}>
        {loading ? (
          <div className={styles.loading}>Loading deleted documents...</div>
        ) : documents.length === 0 ? (
          <div className={styles.emptyState}>
            <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1">
              <polyline points="3 6 5 6 21 6" />
              <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
              <line x1="10" y1="11" x2="10" y2="17" />
              <line x1="14" y1="11" x2="14" y2="17" />
            </svg>
            <h2>Recycle Bin is Empty</h2>
            <p>Deleted documents will appear here and can be restored within 30 days.</p>
          </div>
        ) : (
          <>
            {selectedIds.size > 0 && (
              <div className={styles.selectionBar}>
                <span>{selectedIds.size} item{selectedIds.size !== 1 ? 's' : ''} selected</span>
                <Button variant="secondary" size="sm" onClick={() => setSelectedIds(new Set())}>
                  Clear Selection
                </Button>
              </div>
            )}

            <div className={styles.tableContainer}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.checkboxCell}>
                      <input
                        type="checkbox"
                        checked={selectedIds.size === documents.length && documents.length > 0}
                        onChange={toggleSelectAll}
                      />
                    </th>
                    <th>Document</th>
                    <th>Type</th>
                    <th>Classification</th>
                    <th>Deleted By</th>
                    <th>Deleted</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {documents.map((doc) => {
                    const daysRemaining = getDaysUntilPermanentDelete(doc.deletedAt);
                    return (
                      <tr key={doc.id} className={selectedIds.has(doc.id) ? styles.selected : ''}>
                        <td className={styles.checkboxCell}>
                          <input
                            type="checkbox"
                            checked={selectedIds.has(doc.id)}
                            onChange={() => toggleSelection(doc.id)}
                          />
                        </td>
                        <td>
                          <div className={styles.docCell}>
                            <div className={styles.docIcon}>
                              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                                <path d="M4 4C4 2.89543 4.89543 2 6 2H14L20 8V20C20 21.1046 19.1046 22 18 22H6C4.89543 22 4 21.1046 4 20V4Z" />
                                <path d="M14 2V8H20" />
                              </svg>
                            </div>
                            <div className={styles.docInfo}>
                              <span className={styles.docTitle}>{doc.title}</span>
                              {doc.referenceNumber && (
                                <span className={styles.docRef}>{doc.referenceNumber}</span>
                              )}
                            </div>
                          </div>
                        </td>
                        <td>
                          <span className={styles.typeBadge}>{doc.documentType.name}</span>
                        </td>
                        <td>
                          <span
                            className={styles.classificationBadge}
                            style={{
                              backgroundColor: `${doc.securityClassification.color}20`,
                              color: doc.securityClassification.color,
                            }}
                          >
                            {doc.securityClassification.name}
                          </span>
                        </td>
                        <td>
                          <span className={styles.deletedBy}>
                            {doc.deletedBy.firstName} {doc.deletedBy.lastName}
                          </span>
                        </td>
                        <td>
                          <div className={styles.deletedInfo}>
                            <span className={styles.deletedDate}>{formatDate(doc.deletedAt)}</span>
                            <span className={`${styles.daysRemaining} ${daysRemaining <= 7 ? styles.urgent : ''}`}>
                              {daysRemaining} days remaining
                            </span>
                          </div>
                        </td>
                        <td>
                          <div className={styles.actions}>
                            <button
                              className={`${styles.actionBtn} ${styles.restoreBtn}`}
                              onClick={() => setRestoreDoc(doc)}
                              title="Restore"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <path d="M3 12a9 9 0 1 0 9-9 9.75 9.75 0 0 0-6.74 2.74L3 8" />
                                <path d="M3 3v5h5" />
                              </svg>
                            </button>
                            <button
                              className={`${styles.actionBtn} ${styles.deleteBtn}`}
                              onClick={() => setDeleteDoc(doc)}
                              title="Delete Permanently"
                            >
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                <polyline points="3 6 5 6 21 6" />
                                <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                              </svg>
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className={styles.pagination}>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => Math.max(1, p - 1))}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <span className={styles.pageInfo}>Page {page} of {totalPages}</span>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                  disabled={page === totalPages}
                >
                  Next
                </Button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Restore Confirmation Modal */}
      <Modal
        isOpen={!!restoreDoc}
        onClose={() => setRestoreDoc(null)}
        title="Restore Document"
      >
        <div className={styles.confirmModal}>
          {error && <div className={styles.error}>{error}</div>}
          <p>Are you sure you want to restore <strong>{restoreDoc?.title}</strong>?</p>
          <p className={styles.infoText}>
            The document will be moved back to its original location.
          </p>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setRestoreDoc(null)}>
              Cancel
            </Button>
            <Button onClick={() => restoreDoc && handleRestore(restoreDoc)} disabled={processing}>
              {processing ? 'Restoring...' : 'Restore Document'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Permanent Delete Confirmation Modal */}
      <Modal
        isOpen={!!deleteDoc}
        onClose={() => setDeleteDoc(null)}
        title="Permanently Delete Document"
      >
        <div className={styles.confirmModal}>
          {error && <div className={styles.error}>{error}</div>}
          <p>Are you sure you want to permanently delete <strong>{deleteDoc?.title}</strong>?</p>
          <p className={styles.warningText}>
            This action cannot be undone. The document and all its versions will be permanently removed.
          </p>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setDeleteDoc(null)}>
              Cancel
            </Button>
            <Button
              variant="danger"
              onClick={() => deleteDoc && handlePermanentDelete(deleteDoc)}
              disabled={processing}
            >
              {processing ? 'Deleting...' : 'Delete Permanently'}
            </Button>
          </div>
        </div>
      </Modal>

      {/* Empty Bin Confirmation Modal */}
      <Modal
        isOpen={showEmptyConfirm}
        onClose={() => setShowEmptyConfirm(false)}
        title="Empty Recycle Bin"
      >
        <div className={styles.confirmModal}>
          {error && <div className={styles.error}>{error}</div>}
          <p>Are you sure you want to permanently delete all {totalCount} items in the recycle bin?</p>
          <p className={styles.warningText}>
            This action cannot be undone. All documents will be permanently removed from the system.
          </p>
          <div className={styles.modalActions}>
            <Button variant="secondary" onClick={() => setShowEmptyConfirm(false)}>
              Cancel
            </Button>
            <Button variant="danger" onClick={handleEmptyBin} disabled={processing}>
              {processing ? 'Emptying...' : 'Empty Recycle Bin'}
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
